import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useCallback, useEffect } from "react";
import { workflowLogApi } from "./workflow-log.api";
import { useWorkflowLogStore } from "./workflow-log.store";

export const workflowLogKeys = {
	all: ["workflow-log"] as const,
	executionLogs: (workflowId: string, limit?: number) =>
		[...workflowLogKeys.all, "execution", workflowId, limit] as const,
	errorLogs: (workflowId: string, limit?: number) =>
		[...workflowLogKeys.all, "errors", workflowId, limit] as const,
};

export const useGetExecutionLogs = (
	workflowId: string,
	options?: { limit?: number; polling?: boolean },
) => {
	const setExecutionLogs = useWorkflowLogStore((state) => state.setExecutionLogs);

	const query = useQuery({
		queryKey: workflowLogKeys.executionLogs(workflowId, options?.limit),
		queryFn: () => workflowLogApi.getExecutionLogs(workflowId, options?.limit),
		enabled: Boolean(workflowId),
		refetchInterval: options?.polling ? 5000 : false,
	});

	useEffect(() => {
		if (query.data) setExecutionLogs(query.data.logs);
	}, [query.data, setExecutionLogs]);

	return query;
};

export const useGetErrorLogs = (
	workflowId: string,
	options?: { limit?: number; polling?: boolean },
) => {
	const setErrorLogs = useWorkflowLogStore((state) => state.setErrorLogs);

	const query = useQuery({
		queryKey: workflowLogKeys.errorLogs(workflowId, options?.limit),
		queryFn: () => workflowLogApi.getErrorLogs(workflowId, options?.limit),
		enabled: Boolean(workflowId),
		refetchInterval: options?.polling ? 5000 : false,
	});

	useEffect(() => {
		if (query.data) setErrorLogs(query.data.errors);
	}, [query.data, setErrorLogs]);

	return query;
};

export const useRefreshLogs = (workflowId: string) => {
	const queryClient = useQueryClient();

	return useCallback(() => {
		queryClient.invalidateQueries({ queryKey: workflowLogKeys.executionLogs(workflowId) });
		queryClient.invalidateQueries({ queryKey: workflowLogKeys.errorLogs(workflowId) });
	}, [queryClient, workflowId]);
};
